import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';

import { messageSeverityEnum } from '../../../../../Common/Misc/enums/message-severity.enum';

import { Subscription } from 'rxjs';
import { ButtonTypesEnum } from '../../../../../Common/Forms/enums/button-types.enum';
import { ControlValueAccessorModel, getProvider } from '../../../../../Common/Forms/models/control-value-accessor.model';
import { JournodexSubjectModel, JournodexSubjectParentModel } from '../../../../../Common/Journodex/models/journodex.subject.model';
import {  JournodexSubjectsService, SubjectUtils } from '../../../../../Common/Journodex/services/journodex-subjects.service';
import { GroupedAutoCompleteComponent } from '../../../../Forms/components/GroupedAutocomplete/grouped-autocomplete.component';

enum SubjectChipDropdownButtonTypesEnum {
    collapse,
    browseAll
}

export interface AutocompleteBrowseAllOptions {
    parent?: JournodexSubjectParentModel;
    query?: string;
}

@Component({
    selector: 'agpr-subject-chips',
    templateUrl: './subject-chips.component.html',
    styleUrls: ['./subject-chips.component.scss'],
    providers: [getProvider(SubjectChipsComponent)],
    standalone: false
})
export class SubjectChipsComponent extends ControlValueAccessorModel<any[]> {
    readonly dropdownButtonTypes = SubjectChipDropdownButtonTypesEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;

    subscriptions: Subscription[] = [];
    subjectTree: JournodexSubjectParentModel[] = [];
    /** Object map to store autocomplete group collapsed state */
    groupToggleList: {[key: string]: boolean} = {};

    @Input() id: string;
    @Input() value: any[] = [];
    @Input() placeholder: string = 'Discover.contactEditor.fields.subject.placeholder';
    @Input() ariaLabelledBy: string;

    @Output() browseAll: EventEmitter<void> = new EventEmitter<void>();
    @Output() autocompleteBrowseAllClicked = new EventEmitter<AutocompleteBrowseAllOptions>();

    @ViewChild('autocomplete') autocomplete: GroupedAutoCompleteComponent;
    @ViewChild('chipList') chipList: ElementRef;

    constructor(
        public journodexSubjectsService: JournodexSubjectsService,
    ) {
        super();
    }

    /** Called from browse all */
    writeValue(value: number[] | JournodexSubjectParentModel[]): void {
        if (value) {
            const allParents = this.normalizeParentSubjects(value as JournodexSubjectParentModel[]);
            super.writeValue(allParents);
            this.subjectTree = allParents;
            this.updateSubjectTree();
        }
    }

    registerOnChange(fn: (value: any[]) => void): void {
        this.onModelChange = fn;
    }

    addChip(chip: JournodexSubjectModel): void {
        const foundParent = this.findParentOfChild(chip);
        if (foundParent) {
            if (!foundParent.children.find(child => child.id === chip.id)) {
                foundParent.children.push(chip);
            }
        } else {
            const newParent = chip.parent.clone();
            newParent.children = [chip];
            this.subjectTree.push(newParent);
        }
        this.updateSubjectTree();
    }

    removeParentSubject(chip: JournodexSubjectParentModel): void {
        this.subjectTree = this.subjectTree.filter(parent => parent.id !== chip.id);
        this.updateSubjectTree(true);
    }

    removeChildSubject(chip: JournodexSubjectModel) {
        const parent = this.findParentOfChild(chip);
        if (parent) {
            SubjectUtils.removeChildOfSubjectById(parent, chip.id);
        }
        this.updateSubjectTree();
    }

    /**
     * Some parents may be "old" data, which means they will be parents with no children symbolizing that all children are selected.
     * We need to return proper parents with all children in this case.
     */
    private normalizeParentSubjects(allParents: JournodexSubjectParentModel[]): JournodexSubjectParentModel[] {
        const possibleParentOnlySelections = allParents.filter(subject => {
            return subject.children && subject.children.length === 0;
        });

        if (possibleParentOnlySelections) {
            let normalizedParents: JournodexSubjectParentModel[] = [];
            this.journodexSubjectsService.getAllCachedChildrenOfParents(possibleParentOnlySelections).subscribe(fullParents => {
                normalizedParents = allParents.map(parent => {
                    if (parent.children && parent.children.length === 0) {
                        return fullParents.find(fullParent => fullParent.id === parent.id);
                    }
                    return parent;
                });
            });
            return normalizedParents;
        }
        return allParents;
    }

    private findParentOfChild(child: JournodexSubjectModel): JournodexSubjectParentModel {
        return this.subjectTree.find(parent => parent.id === child.parent.id) as JournodexSubjectParentModel;
    }

    updateSubjectTree(handleFocus: boolean = false) {
        this.subjectTree = this.subjectTree
            .filter(parent => (parent as JournodexSubjectParentModel).children.length > 0)
            .sort((a, b) => (a as JournodexSubjectParentModel).name.localeCompare(b.name))
            .map(parent => {
                (parent as JournodexSubjectParentModel).children.sort((a, b) => a.name.localeCompare(b.name));
                return parent.clone();
            });
        this.value = this.subjectTree;
        this.onModelChange(this.subjectTree);
        if (handleFocus && this.subjectTree.length === 0) {
            this.focusInput();
        }
    }

    handleChipFocus(index: number, groupIndex: number) {
        requestAnimationFrame(() => {
            const groups = this.chipList?.nativeElement.querySelectorAll('.agpr-subject-chips-subjectRow');
            if (!groups) {
                this.focusInput();
                return;
            }
            if (groupIndex === groups.length) {
                groupIndex--;
            }
            if (groups[groupIndex]) {
                let chips = groups[groupIndex].querySelectorAll('.agpr-chip-icon');
                if (!chips) {
                    if (groupIndex === 0) {
                        groupIndex++;
                    } else {
                        groupIndex--;
                    }
                    chips = groups[groupIndex].querySelectorAll('.agpr-chip-icon');
                    if (!chips) {
                        return;
                    }
                    index = chips.length - 1;
                }
                if (index === chips.length) {
                    index--;
                }
                if (chips[index]) {
                    chips[index].focus();
                }
            }
        });

    }

    focusInput() {
        this.autocomplete.autocomplete.inputEL.nativeElement.focus();
    }
}

